import { api, throwApiError } from "../../../api/api";
import type { DpFinanceView } from "./financeBridge";

/**
 * Folha mensal para CONFERÊNCIA (plano F1, ADR 0016). O cálculo serve para
 * conferir a folha do contador — não é holerite.
 *
 * `Decimal` do Prisma chega como STRING (BUG-R05): converter com `Number()` antes
 * de formatar.
 */

export type DpTaxTableType = "INSS" | "IRRF" | "IRRF_REDUCAO";
export type DpIrrfDeductionMode = "AUTOMATICO" | "LEGAL" | "SIMPLIFICADO";
export type DpPayrollItemKind = "PROVENTO" | "DESCONTO" | "INFORMATIVO";
export type DpDeductionCalcMode = "PERCENTUAL_BRUTO" | "PERCENTUAL_LIQUIDO" | "PERCENTUAL_SALARIO_MINIMO" | "VALOR_FIXO";
export type DpPayrollPeriodStatus = "ABERTO" | "FECHADO";
/** Tipo da competência (F2a, ADR 0028): a folha do mês ou uma parcela do 13º. */
export type DpPayrollKind = "MENSAL" | "DECIMO_TERCEIRO_1" | "DECIMO_TERCEIRO_2";
/** RESCINDIDO (F2c): desligado no mês — sem valores, pago no demonstrativo de rescisão. */
export type DpPayrollResultStatus = "OK" | "SEM_PONTO" | "PENDENTE" | "APURACAO_MANUAL" | "RESCINDIDO";

export const TAX_TABLE_TYPE_LABELS: Record<DpTaxTableType, string> = {
  INSS: "INSS (segurado empregado)",
  IRRF: "IRRF (tabela progressiva mensal)",
  IRRF_REDUCAO: "Redução do IRRF (Lei 15.270/2025)",
};

export const IRRF_DEDUCTION_MODE_LABELS: Record<DpIrrfDeductionMode, string> = {
  AUTOMATICO: "Automático (o que der menos imposto)",
  LEGAL: "Sempre deduções legais",
  SIMPLIFICADO: "Sempre desconto simplificado",
};

export const PAYROLL_ITEM_KIND_LABELS: Record<DpPayrollItemKind, string> = {
  PROVENTO: "Provento",
  DESCONTO: "Desconto",
  INFORMATIVO: "Informativo",
};

export const DEDUCTION_CALC_MODE_LABELS: Record<DpDeductionCalcMode, string> = {
  VALOR_FIXO: "Valor fixo mensal",
  PERCENTUAL_BRUTO: "% do bruto",
  PERCENTUAL_LIQUIDO: "% do líquido (bruto − INSS − IR)",
  PERCENTUAL_SALARIO_MINIMO: "% do salário mínimo",
};

export const PAYROLL_KIND_LABELS: Record<DpPayrollKind, string> = {
  MENSAL: "Folha mensal",
  DECIMO_TERCEIRO_1: "13º — 1ª parcela",
  DECIMO_TERCEIRO_2: "13º — 2ª parcela",
};

/** "03/2026" na folha mensal; "13º/2026 (1ª)" e "13º/2026 (2ª)" no 13º. */
export const formatPayrollCompetence = (period: { year: number; month: number; kind?: DpPayrollKind }): string => {
  if (period.kind === "DECIMO_TERCEIRO_1") return `13º/${period.year} (1ª)`;
  if (period.kind === "DECIMO_TERCEIRO_2") return `13º/${period.year} (2ª)`;
  return `${String(period.month).padStart(2, "0")}/${period.year}`;
};

export const isThirteenth = (period: { kind?: DpPayrollKind }) => !!period.kind && period.kind !== "MENSAL";

/** "2026-03" na folha mensal; "13-2026-1a-parcela" no 13º — nome dos arquivos baixados (igual ao do backend). */
export const competenceSlug = (period: { year: number; month: number; kind?: DpPayrollKind }): string => {
  if (period.kind === "DECIMO_TERCEIRO_1") return `13-${period.year}-1a-parcela`;
  if (period.kind === "DECIMO_TERCEIRO_2") return `13-${period.year}-2a-parcela`;
  return `${period.year}-${String(period.month).padStart(2, "0")}`;
};

export const PAYROLL_RESULT_STATUS_LABELS: Record<DpPayrollResultStatus, string> = {
  OK: "OK",
  SEM_PONTO: "Sem apuração",
  PENDENTE: "Pendente",
  APURACAO_MANUAL: "Apuração manual",
  RESCINDIDO: "Rescindido",
};

export const PAYROLL_RESULT_STATUS_BADGE: Record<DpPayrollResultStatus, string> = {
  OK: "dpBadge--green",
  SEM_PONTO: "dpBadge--gray",
  PENDENTE: "dpBadge--orange",
  APURACAO_MANUAL: "dpBadge--green",
  RESCINDIDO: "dpBadge--gray",
};

// ── Tipos ───────────────────────────────────────────────────────────────────

export type DpTaxBracket = {
  id: string;
  order: number;
  upTo: string | null;
  rate: string;
  /** Percentual já convertido pelo backend (7.5 = 7,5%). */
  ratePercent: string;
  deduction: string;
};

export type DpTaxTable = {
  id: string;
  type: DpTaxTableType;
  effectiveFrom: string;
  effectiveTo: string | null;
  dependentDeduction: string | null;
  simplifiedDiscount: string | null;
  notes: string | null;
  createdById: string | null;
  brackets: DpTaxBracket[];
};

export type TaxTablePayload = {
  type: DpTaxTableType;
  effectiveFrom: string;
  dependentDeduction?: number | null;
  simplifiedDiscount?: number | null;
  notes?: string;
  brackets: Array<{ upTo: number | null; ratePercent: number; deduction: number }>;
};

export type DpPayrollPolicy = {
  id: string | null;
  effectiveFrom: string | null;
  isDefault: boolean;
  advanceDay: number;
  paymentBusinessDay: number;
  dailyDivisor: number;
  irrfOnAdvance: boolean;
  irrfDeductionMode: DpIrrfDeductionMode;
  patRegistered: boolean;
  patMaxEmployeePct: string;
  dsrOnOvertime: boolean;
  overtimeWorkdayPct: string;
  overtimeRestDayPct: string;
  nightPct: string;
  fgtsPct: string;
  fgtsApprenticePct: string;
};

export type PayrollPolicyPayload = {
  effectiveFrom: string;
  advanceDay: number;
  paymentBusinessDay: number;
  dailyDivisor: number;
  irrfOnAdvance: boolean;
  irrfDeductionMode: DpIrrfDeductionMode;
  patRegistered: boolean;
  patMaxEmployeePct: number;
  dsrOnOvertime: boolean;
  overtimeWorkdayPct: number;
  overtimeRestDayPct: number;
  nightPct: number;
  fgtsPct: number;
  fgtsApprenticePct: number;
};

export type DpPayrollItem = {
  id: string;
  code: string;
  name: string;
  kind: DpPayrollItemKind;
  isSystem: boolean;
  inInss: boolean;
  inIrrf: boolean;
  inFgts: boolean;
  externalCode: string | null;
  sortOrder: number;
  isActive: boolean;
  /** Aceita lançamento do DP (recorrente ou avulso). */
  isManual: boolean;
};

export type PayrollItemPayload = {
  code?: string;
  name?: string;
  kind?: DpPayrollItemKind;
  inInss?: boolean;
  inIrrf?: boolean;
  inFgts?: boolean;
  externalCode?: string | null;
  sortOrder?: number;
  isActive?: boolean;
};

export type DpRecurringDeduction = {
  id: string;
  contractId: string;
  itemId: string;
  item: { id: string; code: string; name: string; kind: DpPayrollItemKind };
  calcMode: DpDeductionCalcMode;
  percent: string | null;
  amount: string | null;
  startMonth: string;
  endMonth: string | null;
  installments: number | null;
  beneficiary: string | null;
  notes: string | null;
  /** Pensão: desconta também no 13º e nas férias (F2). */
  appliesToThirteenthAndVacation: boolean;
};

export type RecurringDeductionPayload = {
  itemId: string;
  calcMode: DpDeductionCalcMode;
  percent?: number | null;
  amount?: number | null;
  startMonth: string;
  endMonth?: string | null;
  installments?: number | null;
  beneficiary?: string | null;
  notes?: string | null;
  appliesToThirteenthAndVacation?: boolean;
};

export type DpPayrollPeriodListItem = {
  id: string;
  employerId: string;
  employer: { id: string; legalName: string; tradeName: string | null };
  year: number;
  month: number;
  kind: DpPayrollKind;
  status: DpPayrollPeriodStatus;
  /** Nulo no 13º (não há adiantamento). */
  advanceDate: string | null;
  paymentDate: string;
  calculatedAt: string | null;
  closedAt: string | null;
  totals: { contracts: number; gross: string; net: string };
};

export type DpPayrollResultMeta = {
  fullName: string;
  cpf: string | null;
  registrationNumber: string | null;
  costCenter: { code: string; name: string } | null;
  store: string | null;
};

export type DpPayrollResultRow = {
  id: string;
  contractId: string;
  employeeId: string;
  status: DpPayrollResultStatus;
  grossAmount: string;
  deductionsAmount: string;
  netAmount: string;
  advanceAmount: string;
  inssAmount: string;
  irrfAmount: string;
  fgtsAmount: string;
  warnings: string[];
  meta: DpPayrollResultMeta;
};

export type DpPayrollPixCopy = {
  contractId: string;
  copiedAt: string;
  copiedBy: { id: string; nomeCompleto: string } | null;
};

/** Linha da lista da competência: PIX do cadastro atual e a marca de "PIX copiado". */
export type DpPayrollPeriodResult = DpPayrollResultRow & {
  pixKey: string | null;
  pixCopy: DpPayrollPixCopy | null;
};

export type DpPayrollPeriod =Omit<DpPayrollPeriodListItem, "totals"> & {
  closedBy: { id: string; nomeCompleto: string } | null;
  reopenedAt: string | null;
  reopenedBy: { id: string; nomeCompleto: string } | null;
  reopenReason: string | null;
  _count: { entries: number };
  summary: {
    contracts: number;
    /** Colaboradores sem apuração — os totais não os incluem (são parciais). */
    withoutInput: number;
    byStatus: Record<DpPayrollResultStatus, number>;
    gross: string;
    deductions: string;
    net: string;
    advance: string;
    inss: string;
    irrf: string;
    fgts: string;
  };
  reconciliation: {
    fgts: { calculated: string; paid: string | null; difference: string | null };
    inss: { employeesCalculated: string; darf: string | null };
  };
  /** Títulos de previsão no Financeiro e os avisos de divergência (ADR 0029). */
  financeiro: DpFinanceView;
  /** Foto das provisões da competência (só na folha mensal; nulo no 13º). */
  provisionSnapshot?: { count: number } | null;
  results: DpPayrollPeriodResult[];
};

export type DpPayrollResultLine = {
  id: string;
  order: number;
  itemCode: string;
  itemName: string;
  kind: DpPayrollItemKind;
  externalCode: string | null;
  reference: string | null;
  amount: string;
  memo: string;
};

export type DpPayrollResultDetail = Omit<DpPayrollResultRow, "meta"> & {
  inssBase: string;
  irrfBase: string;
  fgtsBase: string;
  meta: DpPayrollResultMeta;
  lines: DpPayrollResultLine[];
};

export type DpPayrollEntry = {
  id: string;
  periodId: string;
  contractId: string;
  itemId: string;
  quantity: string | null;
  amount: string;
  note: string | null;
  item: { id: string; code: string; name: string; kind: DpPayrollItemKind };
  contract: { id: string; registrationNumber: string | null; employee: { id: string; fullName: string } };
};

export type PayrollEntryPayload = {
  contractId: string;
  itemId: string;
  quantity?: number | null;
  amount: number;
  note?: string | null;
};

// ── Chamadas ────────────────────────────────────────────────────────────────

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

/** Encapsula o try/catch padrão do módulo: a mensagem do backend vira o erro. */
async function call<T>(fallback: string, run: () => Promise<{ data: T }>): Promise<T> {
  try {
    const { data } = await run();
    return data;
  } catch (error) {
    throwApiError(error, fallback);
    throw error;
  }
}

// Tabelas de INSS e IR
export const listTaxTablesApi = (token: string) =>
  call<DpTaxTable[]>("Erro ao listar as tabelas.", () => api.get("/dp/tax-tables", auth(token)));
export const createTaxTableApi = (token: string, payload: TaxTablePayload) =>
  call<DpTaxTable>("Erro ao cadastrar a tabela.", () => api.post("/dp/tax-tables", payload, auth(token)));
export const updateTaxTableApi = (token: string, id: string, payload: TaxTablePayload) =>
  call<DpTaxTable>("Erro ao salvar a tabela.", () => api.put(`/dp/tax-tables/${id}`, payload, auth(token)));
export const deleteTaxTableApi = (token: string, id: string) =>
  call<void>("Erro ao excluir a tabela.", () => api.delete(`/dp/tax-tables/${id}`, auth(token)));

// Política de pagamento
export const getPayrollPolicyApi = (token: string, employerId: string) =>
  call<DpPayrollPolicy>("Erro ao carregar a política de pagamento.", () =>
    api.get(`/dp/payroll-policies/${employerId}`, auth(token))
  );
export const savePayrollPolicyApi = (token: string, employerId: string, payload: PayrollPolicyPayload) =>
  call<unknown>("Erro ao salvar a política de pagamento.", () =>
    api.put(`/dp/payroll-policies/${employerId}`, payload, auth(token))
  );

// Rubricas
export const listPayrollItemsApi = (token: string) =>
  call<DpPayrollItem[]>("Erro ao listar as rubricas.", () => api.get("/dp/payroll-items", auth(token)));
export const createPayrollItemApi = (token: string, payload: PayrollItemPayload) =>
  call<DpPayrollItem>("Erro ao criar a rubrica.", () => api.post("/dp/payroll-items", payload, auth(token)));
export const updatePayrollItemApi = (token: string, id: string, payload: PayrollItemPayload) =>
  call<DpPayrollItem>("Erro ao salvar a rubrica.", () => api.patch(`/dp/payroll-items/${id}`, payload, auth(token)));

// Descontos recorrentes
export const listRecurringDeductionsApi = (token: string, contractId: string) =>
  call<DpRecurringDeduction[]>("Erro ao listar os descontos em folha.", () =>
    api.get(`/dp/contracts/${contractId}/recurring-deductions`, auth(token))
  );
export const createRecurringDeductionApi = (token: string, contractId: string, payload: RecurringDeductionPayload) =>
  call<DpRecurringDeduction>("Erro ao cadastrar o desconto.", () =>
    api.post(`/dp/contracts/${contractId}/recurring-deductions`, payload, auth(token))
  );
export const updateRecurringDeductionApi = (token: string, id: string, payload: RecurringDeductionPayload) =>
  call<DpRecurringDeduction>("Erro ao salvar o desconto.", () =>
    api.put(`/dp/recurring-deductions/${id}`, payload, auth(token))
  );
export const deleteRecurringDeductionApi = (token: string, id: string) =>
  call<void>("Erro ao excluir o desconto.", () => api.delete(`/dp/recurring-deductions/${id}`, auth(token)));

// Histórico de variáveis anterior ao sistema (ADR 0034): entra nas médias onde não há folha fechada.
export type DpVariableHistory = {
  id: string;
  contractId: string;
  year: number;
  month: number;
  overtimeWorkdayHours: string;
  overtimeRestDayHours: string;
  otherAmount: string;
  notes: string | null;
  createdBy: { id: string; nomeCompleto: string } | null;
};
export type VariableHistoryPayload = {
  overtimeWorkdayHours: number;
  overtimeRestDayHours: number;
  otherAmount: number;
  notes: string | null;
};
export const listVariableHistoryApi = (token: string, contractId: string) =>
  call<DpVariableHistory[]>("Erro ao listar o histórico de variáveis.", () =>
    api.get(`/dp/contracts/${contractId}/variable-history`, auth(token))
  );
export const saveVariableHistoryApi = (token: string, contractId: string, year: number, month: number, payload: VariableHistoryPayload) =>
  call<DpVariableHistory>("Erro ao salvar o mês do histórico.", () =>
    api.put(`/dp/contracts/${contractId}/variable-history/${year}/${month}`, payload, auth(token))
  );
export const deleteVariableHistoryApi = (token: string, contractId: string, year: number, month: number) =>
  call<void>("Erro ao excluir o mês do histórico.", () =>
    api.delete(`/dp/contracts/${contractId}/variable-history/${year}/${month}`, auth(token))
  );

// Competências
export const listPayrollPeriodsApi = (token: string, year?: number) =>
  call<DpPayrollPeriodListItem[]>("Erro ao listar as competências.", () =>
    api.get("/dp/payroll/periods", { ...auth(token), params: year ? { year } : undefined })
  );
export const createPayrollPeriodApi = (
  token: string,
  payload: { year: number; month?: number; kind?: DpPayrollKind; employerId?: string }
) =>
  call<{ id: string }>("Erro ao abrir a competência.", () => api.post("/dp/payroll/periods", payload, auth(token)));
export const getPayrollPeriodApi = (token: string, id: string) =>
  call<DpPayrollPeriod>("Erro ao carregar a competência.", () => api.get(`/dp/payroll/periods/${id}`, auth(token)));
export const updatePayrollPeriodApi = (token: string, id: string, payload: { advanceDate?: string; paymentDate?: string }) =>
  call<unknown>("Erro ao salvar as datas.", () => api.patch(`/dp/payroll/periods/${id}`, payload, auth(token)));
export const calculatePayrollPeriodApi = (token: string, id: string) =>
  call<DpPayrollPeriod>("Erro ao calcular a competência.", () =>
    api.post(`/dp/payroll/periods/${id}/calculate`, {}, auth(token))
  );
export const closePayrollPeriodApi = (token: string, id: string) =>
  call<DpPayrollPeriod>("Erro ao fechar a competência.", () =>
    api.post(`/dp/payroll/periods/${id}/close`, {}, auth(token))
  );
export const reopenPayrollPeriodApi = (token: string, id: string, reason: string) =>
  call<DpPayrollPeriod>("Erro ao reabrir a competência.", () =>
    api.post(`/dp/payroll/periods/${id}/reopen`, { reason }, auth(token))
  );
export const getPayrollResultApi = (token: string, periodId: string, contractId: string) =>
  call<DpPayrollResultDetail>("Erro ao carregar o resultado.", () =>
    api.get(`/dp/payroll/periods/${periodId}/results/${contractId}`, auth(token))
  );

/** O que forma os totais da competência (sem quem está sem apuração, como os cards). */
export type DpPayrollBreakdown = {
  items: { itemCode: string; itemName: string; kind: DpPayrollItemKind; amount: string; count: number }[];
  totals: { proventos: string; descontos: string };
};
export type DpPayrollBreakdownItem = {
  itemCode: string;
  itemName: string;
  total: string;
  rows: {
    contractId: string;
    employeeId: string;
    meta: DpPayrollResultMeta;
    kind: DpPayrollItemKind;
    reference: string | null;
    amount: string;
    memo: string;
  }[];
};
export const getPayrollBreakdownApi = (token: string, periodId: string) =>
  call<DpPayrollBreakdown>("Erro ao carregar o detalhe dos totais.", () =>
    api.get(`/dp/payroll/periods/${periodId}/breakdown`, auth(token))
  );
export const getPayrollBreakdownItemApi = (token: string, periodId: string, itemCode: string) =>
  call<DpPayrollBreakdownItem>("Erro ao carregar a rubrica.", () =>
    api.get(`/dp/payroll/periods/${periodId}/breakdown/${encodeURIComponent(itemCode)}`, auth(token))
  );

/** CSV como blob: um link direto não mandaria o token. */
export const exportPayrollPeriodApi = (token: string, id: string, format: "resumo" | "analitico") =>
  call<Blob>("Erro ao exportar o CSV.", () =>
    api.get(`/dp/payroll/periods/${id}/export`, { ...auth(token), params: { format }, responseType: "blob" })
  );

/**
 * Contracheque do colaborador em PDF. Vem como blob (um link direto não mandaria o
 * token); por isso o erro do backend (409 sem apuração/pendente) também chega como
 * blob e é lido aqui, senão a mensagem se perderia no `throwApiError`.
 */
/**
 * PDF de uma rota do DP como blob. No erro, o corpo também vem como blob: a
 * mensagem do backend (409/404) é lida de dentro dele.
 */
const getDpPdfApi = async (token: string, path: string, fallback: string, params?: Record<string, string>): Promise<Blob> => {
  try {
    const { data } = await api.get(path, { ...auth(token), params, responseType: "blob" });
    return data;
  } catch (error: any) {
    const body = error?.response?.data;
    if (body instanceof Blob) {
      let message = fallback;
      try {
        const parsed = JSON.parse(await body.text());
        if (typeof parsed?.message === "string") message = parsed.message;
      } catch {
        // corpo não é JSON: fica a mensagem padrão
      }
      throw new Error(message);
    }
    throwApiError(error, fallback);
    throw error;
  }
};

export const getPayslipPdfApi = (token: string, periodId: string, contractId: string): Promise<Blob> =>
  getDpPdfApi(token, `/dp/payroll/periods/${periodId}/results/${contractId}/payslip.pdf`, "Erro ao gerar o contracheque.");

/** Todos os contracheques do mês num PDF (duas vias cada; os recusados na página "Não incluídos"). */
export const getPayslipsBatchPdfApi = (token: string, periodId: string, status?: DpPayrollResultStatus): Promise<Blob> =>
  getDpPdfApi(token, `/dp/payroll/periods/${periodId}/payslips.pdf`, "Erro ao gerar os contracheques do mês.", status ? { status } : undefined);

/** Recibo do adiantamento do colaborador (vale com o mês aberto). */
export const getAdvanceReceiptPdfApi = (token: string, periodId: string, contractId: string): Promise<Blob> =>
  getDpPdfApi(token, `/dp/payroll/periods/${periodId}/results/${contractId}/advance-receipt.pdf`, "Erro ao gerar o recibo do adiantamento.");

/** Todos os recibos do adiantamento do mês num PDF. */
export const getAdvanceReceiptsBatchPdfApi = (token: string, periodId: string, status?: DpPayrollResultStatus): Promise<Blob> =>
  getDpPdfApi(token, `/dp/payroll/periods/${periodId}/advance-receipts.pdf`, "Erro ao gerar os recibos do adiantamento.", status ? { status } : undefined);

// Lançamentos avulsos
export const listPayrollEntriesApi = (token: string, periodId: string) =>
  call<DpPayrollEntry[]>("Erro ao listar os lançamentos.", () =>
    api.get(`/dp/payroll/periods/${periodId}/entries`, auth(token))
  );
export const createPayrollEntryApi = (token: string, periodId: string, payload: PayrollEntryPayload) =>
  call<DpPayrollEntry>("Erro ao lançar.", () => api.post(`/dp/payroll/periods/${periodId}/entries`, payload, auth(token)));
export const updatePayrollEntryApi = (token: string, id: string, payload: Partial<PayrollEntryPayload>) =>
  call<DpPayrollEntry>("Erro ao salvar o lançamento.", () => api.patch(`/dp/payroll/entries/${id}`, payload, auth(token)));
export const deletePayrollEntryApi = (token: string, id: string) =>
  call<void>("Erro ao excluir o lançamento.", () => api.delete(`/dp/payroll/entries/${id}`, auth(token)));

// ── Apuração manual do mês (totais que o espelho de ponto daria, digitados pelo DP) ──

export type DpPayrollMonthInput = {
  id: string;
  periodId: string;
  contractId: string;
  workedDays: number | null;
  absenceDays: number | null;
  dsrLostCount: number | null;
  overtimeWorkdayHours: string | null;
  overtimeRestDayHours: string | null;
  nightHours: string | null;
  lateMinutes: number | null;
  notes: string | null;
  /** Vale-adiantamento dado no mês (só na folha mensal): valor e nº de parcelas, os dois ou nenhum. */
  cashAdvanceAmount?: string | null;
  cashAdvanceInstallments?: number | null;
  createdBy: { id: string; nomeCompleto: string } | null;
  createdAt: string;
  updatedAt: string;
};

export type PayrollMonthInputPayload = {
  workedDays?: number | null;
  absenceDays?: number | null;
  dsrLostCount?: number | null;
  overtimeWorkdayHours?: number | null;
  overtimeRestDayHours?: number | null;
  nightHours?: number | null;
  lateMinutes?: number | null;
  notes?: string | null;
  cashAdvanceAmount?: number | null;
  cashAdvanceInstallments?: number | null;
};

export const listPayrollMonthInputsApi = (token: string, periodId: string) =>
  call<DpPayrollMonthInput[]>("Erro ao carregar as apurações do mês.", () =>
    api.get(`/dp/payroll/periods/${periodId}/month-inputs`, auth(token))
  );
export const upsertPayrollMonthInputApi = (token: string, periodId: string, contractId: string, payload: PayrollMonthInputPayload) =>
  call<DpPayrollMonthInput>("Erro ao salvar a apuração.", () =>
    api.put(`/dp/payroll/periods/${periodId}/month-inputs/${contractId}`, payload, auth(token))
  );
export const deletePayrollMonthInputApi = (token: string, periodId: string, contractId: string) =>
  call<void>("Erro ao excluir a apuração.", () =>
    api.delete(`/dp/payroll/periods/${periodId}/month-inputs/${contractId}`, auth(token))
  );

/** O que o sistema já sabe do colaborador no mês — para o DP revisar antes de confirmar. */
export type DpPayrollMonthInputContext = {
  year: number;
  month: number;
  businessDays: number;
  restDays: number;
  contractStart: string;
  contractEnd: string;
  scaleDays: number;
  /** ESCALA = dias de trabalho da escala publicada da loja; PADRAO = segunda a sábado menos feriados. */
  scaleSource?: "PADRAO" | "ESCALA";
  unpaid: Array<{ startDate: string; endDate: string; reason: "FERIAS" | "INSS" | "SEM_REMUNERACAO"; days: number }>;
  unpaidDays: number;
  derivedWorkedDays: number;
  /** Afastamentos justificados pagos pela empresa: não descontam nem tiram dia. */
  paidLeaves: Array<{ startDate: string; endDate: string; days: number }>;
  current: DpPayrollMonthInput | null;
  /** Parcelas dos vales-adiantamento de meses anteriores que caem neste mês (só na folha mensal). */
  cashAdvancesDue?: Array<{ originYear: number; originMonth: number; number: number; of: number; amount: string }>;
};

export const getPayrollMonthInputContextApi = (token: string, periodId: string, contractId: string) =>
  call<DpPayrollMonthInputContext>("Erro ao carregar o que o sistema sabe do mês.", () =>
    api.get(`/dp/payroll/periods/${periodId}/month-inputs/${contractId}/context`, auth(token))
  );
export const confirmRemainingPayrollMonthInputsApi = (token: string, periodId: string) =>
  call<{ confirmed: number }>("Erro ao confirmar as apurações.", () =>
    api.post(`/dp/payroll/periods/${periodId}/month-inputs/confirm-remaining`, {}, auth(token))
  );
export const markPayrollPixCopiedApi = (token: string, periodId: string, contractId: string) =>
  call<DpPayrollPixCopy>("Erro ao marcar o PIX como copiado.", () =>
    api.put(`/dp/payroll/periods/${periodId}/results/${contractId}/pix-copy`, {}, auth(token))
  );
export const unmarkPayrollPixCopiedApi = (token: string, periodId: string, contractId: string) =>
  call<void>("Erro ao desmarcar o PIX.", () =>
    api.delete(`/dp/payroll/periods/${periodId}/results/${contractId}/pix-copy`, auth(token))
  );
